import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import PropTypes from 'prop-types'
import { usePopper } from 'react-popper'
import { DotsMenuIcon } from 'images'
import ActionMenu from './ActionMenu'
import ProgressBar from './ProgressBar'
import { fileStatus } from 'components/Recommendations/Campaign/Media/media'
import { useTranslation } from 'react-i18next'

const POPPER_OPTIONS = {
  placement: 'bottom-end',
  strategy: 'fixed',
  modifiers: [
    { name: 'offset', options: { offset: [0, 4] } },
    { name: 'preventOverflow', options: { padding: 8 } },
  ],
}

const MediaCardContent = ({ item, actionMenuItems, isSelectiveMode, onRemove }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [referenceElement, setReferenceElement] = useState(null)
  const [popperElement, setPopperElement] = useState(null)
  const { t } = useTranslation()
  const { styles, attributes } = usePopper(referenceElement, popperElement, POPPER_OPTIONS)

  useEffect(() => {
    if (!isMenuOpen) return undefined

    const handleClickOutside = (event) => {
      if (referenceElement?.contains(event.target) || popperElement?.contains(event.target)) {
        return
      }

      setIsMenuOpen(false)
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isMenuOpen, referenceElement, popperElement])

  const progress = item.progress ?? 0
  const isUploading = item.status === fileStatus.new || item.status === fileStatus.uploading
  const isError = item.status === fileStatus.uploadFailed

  return (
    <div className="media-list-item__content">
      <div className="media-list-item__info">
        <p className="title">{item.filename || item.name}</p>

        {isUploading && <ProgressBar progress={progress} />}
        {isError && <p className="error-message" onClick={onRemove ? () => onRemove(item) : undefined}>{t('Recommendations.campaign.setup.media.removeMedia')}</p>}
        {!isError && !isUploading && <p className="size">{item.ratio} • {item.width} x {item.height}</p>}
      </div>

      {!isUploading && !isSelectiveMode && actionMenuItems?.length > 0 && (
        <div className="media-list-item__actions">
          <button
            ref={setReferenceElement}
            className="dots-menu-btn"
            type="button"
            aria-expanded={isMenuOpen}
            aria-haspopup="true"
            onClick={(event) => {
              event.stopPropagation()
              setIsMenuOpen((open) => !open)
            }}
          >
            <DotsMenuIcon />
          </button>

          {isMenuOpen && createPortal(
            <div
              ref={setPopperElement}
              className="media-action-menu-popper"
              style={styles.popper}
              {...attributes?.popper}
            >
              <ActionMenu
                actionMenuItems={actionMenuItems}
                setIsMenuOpen={setIsMenuOpen}
              />
            </div>,
            document.body,
          )}
        </div>
      )}
    </div>
  )
}

MediaCardContent.propTypes = {
  item: PropTypes.object.isRequired,
  actionMenuItems: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
  })),
  onRemove: PropTypes.func,
  isSelectiveMode: PropTypes.bool,
}

export default MediaCardContent
